import { useMemo } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { supabase } from "../lib/supabase";
import type { PublishedPost } from "../lib/supabase";
import { useSupabaseQuery } from "../hooks/useSupabaseQuery";
import { fmtInt } from "../lib/format";

const LANE_COLORS: Record<number, string> = { 1: "#f59e0b", 2: "#3b82f6", 3: "#8b5cf6" };

export default function Lanes() {
  const since = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString();
  }, []);

  const { data, error, loading } = useSupabaseQuery<PublishedPost[]>(
    async () =>
      supabase
        .from("published_posts")
        .select("id, lane, synth_at")
        .gte("synth_at", since)
        .order("synth_at", { ascending: true })
        .limit(10_000) as unknown as Promise<any>,
    [since],
    60_000,
  );

  const { byHour, totals } = useMemo(() => {
    const rows = data || [];
    const byHour: Record<string, { hour: string; lane1: number; lane2: number; lane3: number }> = {};
    const totals: Record<number, number> = { 1: 0, 2: 0, 3: 0 };
    for (const r of rows) {
      const ts = (r.synth_at || "").slice(0, 13);
      if (!ts) continue;
      if (!byHour[ts]) byHour[ts] = { hour: ts, lane1: 0, lane2: 0, lane3: 0 };
      const k = r.lane === 1 ? "lane1" : r.lane === 2 ? "lane2" : r.lane === 3 ? "lane3" : null;
      if (k) byHour[ts][k]++;
      if (r.lane && r.lane in totals) totals[r.lane]++;
    }
    return {
      byHour: Object.values(byHour).sort((a, b) => a.hour.localeCompare(b.hour)),
      totals,
    };
  }, [data]);

  const pieData = [
    { name: "Lane 1", value: totals[1], color: LANE_COLORS[1] },
    { name: "Lane 2", value: totals[2], color: LANE_COLORS[2] },
    { name: "Lane 3", value: totals[3], color: LANE_COLORS[3] },
  ].filter((d) => d.value > 0);

  const total = totals[1] + totals[2] + totals[3];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[1, 2, 3].map((ln) => {
          const pct = total ? Math.round((totals[ln] / total) * 100) : 0;
          return (
            <div key={ln} className="card">
              <p className="text-xs uppercase tracking-wider" style={{ color: LANE_COLORS[ln] }}>
                Lane {ln}
              </p>
              <p className="text-2xl font-semibold mt-1 font-mono">{fmtInt(totals[ln])}</p>
              <p className="text-xs text-chrome-400 mt-0.5">{pct}% of last 14 days</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card lg:col-span-2">
          <h3 className="text-sm font-medium mb-3">Hourly volume by lane (14d)</h3>
          {loading && !data && <p className="text-sm text-chrome-400">Loading…</p>}
          {error && <p className="text-sm text-bad">{(error as Error).message}</p>}
          {byHour.length === 0 && data && (
            <p className="text-sm text-chrome-400">No posts yet.</p>
          )}
          {byHour.length > 0 && (
            <div className="h-72">
              <ResponsiveContainer>
                <AreaChart data={byHour}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#26262a" />
                  <XAxis dataKey="hour" stroke="#76767c" fontSize={10} />
                  <YAxis stroke="#76767c" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#111113", border: "1px solid #3a3a40", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="lane1" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.3} name="Lane 1" />
                  <Area type="monotone" dataKey="lane2" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} name="Lane 2" />
                  <Area type="monotone" dataKey="lane3" stackId="1" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.3} name="Lane 3" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="card">
          <h3 className="text-sm font-medium mb-3">Distribution</h3>
          {pieData.length === 0 ? (
            <p className="text-sm text-chrome-400">—</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#111113", border: "1px solid #3a3a40", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
